import React, { FunctionComponent, memo, useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import { isEmpty } from 'ts-utils';
import { Color } from '../../../config/Color';
import { ButtonInfoContainerVariation } from '../../styles/Containers';
import { CopyContainer } from '../../styles/CopyContainer';
import { NextButton } from '../../styles/LayoutButtons';
import { Footnote, HeroTitle } from '../../styles/Typography';
import { ANIMATION_CONSTANTS } from '../shared/AnimationConstants';
import { PresentationContent, renderContent } from '../shared/AnimationUtils';

export type { PresentationContent } from '../shared/AnimationUtils';

export type PresentationLayoutConfig = {
  title?: string;
  content: PresentationContent[];
  primaryButtonTitle?: string;
  buttonHook?: PresentationContent[];
  footnotes?: string[];
  textAnimationEnabled?: boolean;
};

export type PresentationLayoutProps = PresentationLayoutConfig & {
  primaryButtonDisabled?: boolean;
  onPressPrimaryButton?: () => void;
};

export const PresentationLayout: FunctionComponent<PresentationLayoutProps> = memo(
  function PresentationLayout(props) {
    const {
      title,
      content,
      buttonHook,
      footnotes,
      primaryButtonDisabled,
      primaryButtonTitle,
      onPressPrimaryButton,
      textAnimationEnabled = false,
    } = props;

    // Track which item is currently visible in the animation sequence
    // If animations are disabled, set to special value to show all items
    const [visibleItemIndex, setVisibleItemIndex] = useState(
      textAnimationEnabled ? -1 : ANIMATION_CONSTANTS.SHOW_ALL_ITEMS_INDEX
    );
    // Track if the current animated item has completed its animation
    const [isCurrentItemComplete, setIsCurrentItemComplete] = useState(false);
    // Keep track of which items have already completed their animations
    const [completedItems, setCompletedItems] = useState<Set<number>>(new Set());

    // Calculate counts of text items for animation sequencing
    const { textItemsBeforeContent, totalTextItems } = useMemo(() => {
      // Count the title as one item if it exists
      const beforeContent = !isEmpty(title) ? 1 : 0;

      // Count only the text-based items in content
      const inContent = content.filter(
        (item) => item.type === 'text' || item.type === 'heading' || item.type === 'smalltext'
      ).length;

      return {
        // Number of text items before the main content (title)
        textItemsBeforeContent: beforeContent,
        // Total number of text items including title
        totalTextItems: beforeContent + inContent,
      };
    }, [title, content]);

    // Effect: Move to the next item in the animation sequence when current item is complete
    useEffect(
      function advanceToNextItem() {
        if (!textAnimationEnabled) return;

        if (isCurrentItemComplete && visibleItemIndex < totalTextItems + content.length) {
          setIsCurrentItemComplete(false);
          setVisibleItemIndex((prev) => prev + 1);
        }
      },
      [
        isCurrentItemComplete,
        visibleItemIndex,
        totalTextItems,
        content.length,
        textAnimationEnabled,
      ]
    );

    // Effect: Start the animation sequence by showing the first item
    useEffect(
      function startAnimation() {
        // Only start animation if enabled and we're at the initial state (-1)
        if (textAnimationEnabled && visibleItemIndex === -1) {
          setVisibleItemIndex(0);
        }
      },
      [textAnimationEnabled, visibleItemIndex]
    );

    // Effect: Automatically advance non-text items (like images) after a delay
    useEffect(
      function autoAdvanceNonTextItems() {
        if (!textAnimationEnabled) return;

        // Check if we're within the valid range of items to display
        if (visibleItemIndex >= 0 && visibleItemIndex < textItemsBeforeContent + content.length) {
          // Adjust index to account for title (if present)
          const currentItemIndex = visibleItemIndex - textItemsBeforeContent;

          // Check if current item is within the content array
          if (currentItemIndex >= 0 && currentItemIndex < content.length) {
            const currentItem = content[currentItemIndex];
            // Determine if this is a text-based item
            const isTextItem =
              currentItem.type === 'text' ||
              currentItem.type === 'heading' ||
              currentItem.type === 'smalltext';

            // For non-text items (images, etc.), automatically advance after a delay.
            if (!isTextItem) {
              const timer = setTimeout(() => {
                setIsCurrentItemComplete(true);
              }, ANIMATION_CONSTANTS.AUTO_ADVANCE_DELAY);

              // Clean up the timer if component unmounts
              return () => clearTimeout(timer);
            }
          }
        }
      },
      [visibleItemIndex, content, textAnimationEnabled, textItemsBeforeContent, totalTextItems]
    );

    /**
     * Handles completion of an animated item
     * Marks the item as complete and signals to advance to the next item
     */
    const handleItemComplete = () => {
      // Add this item to the set of completed items
      setCompletedItems((prev) => new Set([...prev, visibleItemIndex]));
      // Signal that the current item is complete (triggers advance to next)
      setIsCurrentItemComplete(true);
    };

    /**
     * Determines whether an item should be shown based on the current animation state
     * @param index - The index of the item to check
     * @returns true if the item should be visible
     */
    const shouldShowItem = (index: number) => {
      // If animations are disabled, show all items
      if (!textAnimationEnabled) return true;
      // Otherwise, only show items up to the current visible index
      return index <= visibleItemIndex;
    };

    return (
      <div style={{ textAlign: 'left' }} data-test="infoCopy">
        {!isEmpty(title) && shouldShowItem(0) && (
          <div style={{ margin: 'auto', marginBottom: '24px' }}>
            <HeroTitle
              animationEnabled={textAnimationEnabled && !completedItems.has(0)}
              animationIndex={0}
              onAnimationComplete={visibleItemIndex === 0 ? handleItemComplete : undefined}
            >
              {title}
            </HeroTitle>
          </div>
        )}

        {content.map((item, index) => {
          const itemIndex = textItemsBeforeContent + index;
          if (!shouldShowItem(itemIndex)) return null;

          const isTextItem =
            item.type === 'text' || item.type === 'heading' || item.type === 'smalltext';

          const isCurrentItem = itemIndex === visibleItemIndex;

          return renderContent({
            content: item,
            index,
            textAnimationEnabled:
              textAnimationEnabled && isTextItem && !completedItems.has(itemIndex),
            animationIndex: 0, // Reset animation index for each individual text item
            onAnimationComplete:
              (isTextItem && isCurrentItem) === true ? handleItemComplete : undefined,
            shouldAnimateNonText: textAnimationEnabled && !isTextItem && isCurrentItem, // Animate non-text items when they're current
          });
        })}

        {shouldShowItem(textItemsBeforeContent + content.length) &&
          onPressPrimaryButton !== undefined && (
            <ButtonContainer infoContainer={buttonHook !== undefined}>
              <FrontNextButton disabled={primaryButtonDisabled} onClick={onPressPrimaryButton}>
                {primaryButtonTitle ?? 'Got it!'}
              </FrontNextButton>

              {buttonHook !== undefined && (
                <ButtonInfoContainerVariation>
                  {buttonHook?.map((item, index) =>
                    renderContent({
                      content: item,
                      index,
                      textAnimationEnabled: false,
                      animationIndex: 0,
                      onAnimationComplete: undefined,
                      shouldAnimateNonText: false,
                    })
                  )}
                </ButtonInfoContainerVariation>
              )}
            </ButtonContainer>
          )}

        {shouldShowItem(textItemsBeforeContent + content.length) &&
          footnotes !== undefined &&
          footnotes.length > 0 && (
            <FootnotesContainer>
              {footnotes.map((footnote, i) => (
                <Footnote key={i}>
                  <sup>{i + 1}</sup>
                  {footnote}
                </Footnote>
              ))}
            </FootnotesContainer>
          )}
      </div>
    );
  }
);

type ButtonContainerProps = { infoContainer?: boolean };
export const ButtonContainer = styled.div<ButtonContainerProps>`
  padding: ${({ infoContainer }) => (infoContainer ? '170px 0px 7px 0px' : '40px 0px 40px 0px')};
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const FrontNextButton = styled(NextButton)`
  z-index: 1;
`;

const FootnotesContainer = styled(CopyContainer)`
  border-top: 1px solid ${Color.decorative.default};
  margin: 0 auto;
  padding: 2em 0;
`;
